import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { EnvelopeIcon, ShieldCheckIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { sendEmailOTP, verifyEmailOTP } from "../../hooks/useAuth";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { REGION_CONFIG } from "../../config/region";

const EmailOTPLoginForm = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [focusedField, setFocusedField] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const otpInputRef = useRef(null);

  useEffect(() => {
    if (step === "otp" && otpInputRef.current) {
      setTimeout(() => {
        otpInputRef.current.focus();
        otpInputRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 300);
    }
  }, [step]);

  const handleSendEmailOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error(t('auth.invalidEmail'));
      setIsLoading(false);
      return;
    }

    try {
      // ✅ FIXED: Pass current region from config
      console.log('🎯 Sending email OTP with region:', REGION_CONFIG.region);

      const result = await sendEmailOTP({
        email,
        region: REGION_CONFIG.region // ✅ Use current website region
      });

      if (result.success) {
        toast.success(result.data?.message || t('auth.otpSent'));
        setStep("otp");
      } else {
        toast.error(result.error || t('auth.otpSendFailed'));
      }
    } catch (error) {
      console.error('Send email OTP error:', error);
      toast.error(t('auth.otpSendFailed'));
    }
    setIsLoading(false);
  };

  const handleVerifyEmailOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (!otp || otp.length !== 6) {
      toast.error(t('auth.otpInvalid6Digit'));
      setIsLoading(false);
      return;
    }

    try {
      // ✅ FIXED: Pass current region from config
      console.log('🎯 Verifying email OTP with region:', REGION_CONFIG.region);

      const result = await verifyEmailOTP({
        email,
        otp,
        region: REGION_CONFIG.region // ✅ Use current website region
      });

      if (result.success) {
        toast.success(t('auth.loginSuccessRedirecting'));
        // v68.278 — honor sessionStorage 'post_login_redirect' so bookmark
        // login flows return to the event page instead of homepage.
        const postLoginRedirect = sessionStorage.getItem('post_login_redirect');
        sessionStorage.removeItem('post_login_redirect');
        const target = postLoginRedirect && postLoginRedirect.startsWith('/') ? postLoginRedirect : '/';
        setTimeout(() => { window.location.href = target; }, 500);
      } else {
        toast.error(result.error || t('auth.otpInvalid'));
      }
    } catch (error) {
      console.error('Verify email OTP error:', error);
      toast.error(t('auth.otpVerifyFailed'));
    }
    setIsLoading(false);
  };

  const handleResendOTP = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      // ✅ FIXED: Pass current region from config
      const result = await sendEmailOTP({
        email,
        region: REGION_CONFIG.region
      });

      if (result.success) {
        toast.success(t('auth.otpResent'));
        setOtp("");
        if (otpInputRef.current) {
          otpInputRef.current.focus();
        }
      } else {
        toast.error(result.error || t('auth.otpResendFailed'));
      }
    } catch (error) {
      toast.error(t('auth.otpResendFailed'));
    }
    setIsLoading(false);
  };

  return (
    <div style={{ width: "100%", minHeight: "360px", position: "relative" }}>
      {step === "email" && (
        <motion.form
          key="email"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          style={{ width: "100%" }}
          onSubmit={handleSendEmailOTP}
        >
          <label
            style={{
              display: "block",
              marginBottom: 8,
              fontWeight: 500,
              color: "#374151",
            }}
          >
            <EnvelopeIcon
              style={{ height: 16, width: 16, marginRight: 8, display: 'inline' }}
            />
            {t('auth.emailAddressLabel')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocusedField('email')}
            onBlur={() => setFocusedField(null)}
            placeholder={t('auth.emailPlaceholder')}
            style={{
              width: "100%",
              padding: 12,
              border: focusedField === 'email' ? "2px solid #184A99" : "1px solid #d1d5db",
              borderRadius: "12px",
              fontSize: 16,
              marginBottom: 12,
              outline: "none",
              backgroundColor: focusedField === 'email' ? 'white' : '#fafafa',
              transition: 'all 0.3s ease',
              boxSizing: 'border-box'
            }}
          />
          {/* EV-1245 — soft Gmail→Google nudge. Policy (Patrick): never entice extra
              providers; ONLY steer a Gmail address toward the Google button so the
              same person doesn't end up with two accounts (email-OTP + Google are
              auto-linked by verified email, but Google is one tap and no code).
              Non-blocking: the OTP path stays fully usable. */}
          {/@(gmail|googlemail)\.com\s*$/i.test(email) && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                background: "#fef3c7",
                border: "1px solid #fcd34d",
                borderRadius: 10,
                fontSize: 13,
                color: "#92400e",
                lineHeight: 1.4,
              }}
            >
              💡 {t('auth.gmailNudgePrefix')} <b>{t('auth.signInGoogle')}</b> {t('auth.gmailNudgeSuffix')}
            </div>
          )}
          <p
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginBottom: 20,
            }}
          >
            {t('auth.sendCodeHint')}
          </p>

          <button
            type="submit"
            disabled={isLoading || !email}
            style={{
              width: "100%",
              background: "linear-gradient(to right, #f59e0b, #ea580c)",
              color: "white",
              padding: 12,
              borderRadius: 12,
              fontWeight: 600,
              fontSize: 18,
              border: "none",
              cursor: isLoading || !email ? "not-allowed" : "pointer",
              opacity: isLoading || !email ? 0.5 : 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isLoading ? (
              <>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    border: "2px solid white",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginRight: 8,
                  }}
                ></div>
                {t('auth.sendingOtp')}
              </>
            ) : (
              <>
                {t('auth.sendOtp')}
                <ArrowRightIcon style={{ height: 20, width: 20, marginLeft: 8 }} />
              </>
            )}
          </button>
        </motion.form>
      )}

      {step === "otp" && (
        <motion.form
          key="otp"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          style={{ width: "100%" }}
          onSubmit={handleVerifyEmailOTP}
        >
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <div
              style={{
                width: 64,
                height: 64,
                margin: "0 auto 16px",
                borderRadius: "50%",
                backgroundColor: "#fef3c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShieldCheckIcon
                style={{ height: 32, width: 32, color: "#d97706" }}
              />
            </div>
            <h3
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 8,
              }}
            >
              {t('auth.enterVerificationCode')}
            </h3>
            <p style={{ fontSize: 14, color: "#6b7280" }}>
              {t('auth.codeSentTo', { email })}
            </p>
          </div>
          <label
            style={{
              display: "block",
              marginBottom: 8,
              fontWeight: 500,
              color: "#374151",
            }}
          >
            {t('auth.verificationCodeLabel')}
          </label>
          <input
            ref={otpInputRef}
            type="text"
            inputMode="numeric"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder="000000"
            maxLength={6}
            autoFocus
            autoComplete="one-time-code"
            style={{
              width: "100%",
              padding: 16,
              fontSize: 32,
              letterSpacing: 8,
              borderRadius: 12,
              border: "3px solid #f59e0b",
              textAlign: "center",
              fontFamily: "monospace",
              outline: "none",
              backgroundColor: "white",
              color: "#1f2937",
              boxSizing: 'border-box'
            }}
          />
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginTop: 8,
              textAlign: "center",
              background: "#f3f4f6",
              padding: 8,
              borderRadius: 6,
            }}
          >
            {otp.length === 6
              ? t('auth.readyToVerify')
              : t('auth.needMoreDigits', { count: 6 - otp.length })}
          </div>
          <button
            type="submit"
            disabled={isLoading || otp.length !== 6}
            style={{
              width: "100%",
              background: "linear-gradient(to right, #f59e0b, #ea580c)",
              color: "white",
              padding: 12,
              borderRadius: 12,
              fontWeight: 600,
              fontSize: 18,
              border: "none",
              cursor: isLoading || otp.length !== 6 ? "not-allowed" : "pointer",
              opacity: isLoading || otp.length !== 6 ? 0.5 : 1,
              marginTop: 20,
            }}
          >
            {isLoading ? t('auth.verifying') : t('auth.verifyAndLogin')}
          </button>
          <div
            style={{
              marginTop: 24,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={isLoading}
              style={{
                background: "none",
                border: "none",
                color: "#d97706",
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {t('auth.resendOtp')}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("email");
                setOtp("");
              }}
              style={{
                background: "none",
                border: "none",
                color: "#6b7280",
                cursor: "pointer",
              }}
            >
              ← {t('auth.backToEmail')}
            </button>
          </div>
          <style>{`
        @keyframes spin {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
        </motion.form>
      )}
    </div>
  );
};

export default EmailOTPLoginForm;
